import {
  createComponentAdmin,
  OptionalString,
  StringEnum,
  type ModuleCategory,
  type SchemaTypeHelper,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';
import {timezones} from './timezones';

export const reactName = 'AddEvent';
export const name = 'Add Event';
export const description = 'Add Event';
const category: ModuleCategory = 'plugin';

const listStyleEnum = [
  'dropdown',
  'dropdown-static',
  'overlay',
  'modal',
] as const;

const buttonStyleEnum = [
  'default',
  '3d',
  'flat',
  'round',
  'neumorphism',
  'text',
  'date',
  'custom',
  'none',
] as const;

export const schema = Type.Object({
  title: Type.String({
    title: 'Title',
    default: 'My Custom Event',
  }),
  description: OptionalString({
    title: 'Title',
    default: 'Description of the event',
  }),
  location: OptionalString({
    title: 'Location',
    description:
      'The location of the event, this can be a physical address or a URL',
  }),
  organizer: OptionalString({
    title: 'Organizer',
    description:
      'If an organizer is specified, an organizer email must also be specified',
  }),
  organizerEmail: OptionalString({
    title: 'Organizer Email',
  }),
  startTime: Type.String({
    title: 'Start Time',
    format: 'date-time',
    default: '2025-02-25T20:25:20.252Z',
  }),
  endTime: Type.String({
    title: 'End Time',
    format: 'date-time',
    default: '2025-02-25T22:25:20.252Z',
  }),
  // See list of timezones: https://gist.github.com/diogocapela/12c6617fc87607d11fd62d2a4f42b02a
  timezone: StringEnum(timezones, {
    title: 'Time Zone',
    description: 'The time zone of the event.',
    default: 'US/Eastern',
  }),
  buttonStyle: StringEnum(buttonStyleEnum, {
    title: 'Button Style',
    default: 'default',
  }),
  listStyle: StringEnum(listStyleEnum, {
    title: 'List Style',
    default: 'dropdown',
  }),
  buttonLabel: OptionalString({
    title: 'Button Label',
    default: '',
  }),
  buttonColor: OptionalString('Button Color'),
  theme: StringEnum(['dark', 'light'] as const, {
    title: 'Theme',
    default: 'light',
  }),
});

export const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              [
                'Event Details',
                [
                  'title',
                  'description',
                  'location',
                  'organizer',
                  'organizerEmail',
                ],
              ],
              ['Event Schedule', ['timezone', 'startTime', 'endTime']],
              [
                'Appearance',
                [
                  'buttonLabel',
                  'buttonColor',
                  'theme',
                  'buttonStyle',
                  'listStyle',
                ],
              ],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [['Custom Styles', ['styleAttr']]],
          },
        ],
      ],
      ['Animations', []],
    ],
  },
  buttonColor: {
    'ui:widget': 'color',
  },
  theme: {
    'ui:widget': 'LayoutWidget',
    'ui:options': {
      inline: true,
    },
  },
};

export const defaultFieldData: Static<typeof schema> = {
  title: schema.properties.title.default,
  startTime: schema.properties.endTime.default,
  endTime: schema.properties.endTime.default,
  timezone: schema.properties.timezone.default,
  theme: schema.properties.theme.default,
  buttonLabel: schema.properties.buttonLabel.default,
  buttonStyle: schema.properties.buttonStyle.default,
  listStyle: schema.properties.listStyle.default,
};

export const ComponentDefinition = createComponentAdmin({
  id: '5dac188c-72ea-44f7-9f41-67c3df8a3653',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  instructions: Type.Union([]),
  slotConfiguration: {},
  schema,
  uiSchema,
  category,
})
  .withAnimationStates()
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
